<script setup lang="ts">
import DirectUploadDropzone from "@/components/DirectUploadDropzone.vue";
import StockImageSearchModal from "@/components/StockImageSearchModal.vue";
import SpecialCharactersModal from "@/components/TipTapEditor/SpecialCharactersModal.vue";
import ToolbarButton from "@/components/TipTapEditor/ToolbarButton.vue";
import ToolbarDropdown from "@/components/TipTapEditor/ToolbarDropdown.vue";
import ToolbarFontSizeDropdown from "@/components/TipTapEditor/ToolbarFontSizeDropdown.vue";
import type { File, FileInput } from "@/services/fobizzToolsApi";
import { FONT_FAMILIES, FONT_SIZES } from "@/utils/fonts";
import { type Editor, type EditorEvents } from "@tiptap/core";

import { type ToolbarGroup, type ToolbarMode } from "./types";
import useToolbarActions from "./useToolbarActions";

const { t } = useI18n();
const userStore = useUserStore();

const props = withDefaults(
  defineProps<{
    editor: Editor;
    toolbarMode?: ToolbarMode;
    disabledToolbarGroups?: string[];
    size?: "xs" | "sm" | "md" | "lg";
    navbar?: boolean;
    naked?: boolean;
    groups?: ToolbarGroup[];
    parentType?: string;
    parentId?: string;
  }>(),
  {
    toolbarMode: "md",
    disabledToolbarGroups: () => [],
    size: "md",
    navbar: false,
    naked: false,
    groups: () => [
      "header",
      "format",
      "undoRedo",
      "list",
      "align",
      "link",
      "advanced",
      "table",
      // only used by website tool, default is without embed
      // "embed",
    ],
    parentType: undefined,
    parentId: undefined,
  }
);

const {
  formatButtons,
  formatAdvancedButtons,
  undoRedoButtons,
  listButtons,
  alignButtons,
  linkButtons,
  advancedButtons,
  tableButtons,
  embedButtons,
  matchToolbarMode,
  currentTextAlign,
  currentFormatAdvanced,
  changeTextAlign,
  currentFontSize,
  currentFontFamilyLabel,
  changeFontFamily,
  incrementFontSize,
  decrementFontSize,
  changeBlockType,
  changeFontSize,
  insertCharacter,
  isSpecialCharactersModalOpen,
} = useToolbarActions(props.editor, props.groups, props.disabledToolbarGroups);

const attachmentMarkedForDeletion = ref<Set<string>>(new Set());

const handleEditorUpdate = ({ transaction }: EditorEvents["update"]) => {
  const blobSids = new Set<string>();
  transaction.doc.forEach((node) => {
    if (node.attrs.blobSid) {
      blobSids.add(node.attrs.blobSid);
      attachmentMarkedForDeletion.value.delete(node.attrs.blobSid);
    }
  });
  transaction.before.forEach((node) => {
    if (node.attrs.blobSid && !blobSids.has(node.attrs.blobSid)) {
      attachmentMarkedForDeletion.value.add(node.attrs.blobSid);
    }
  });
};

const handleEditorDestroy = () => {
  attachmentMarkedForDeletion.value.forEach(async (blobSid) => {
    await imageAttachtmentService.deleteImageAttachment({
      id: blobSid,
    });
    attachmentMarkedForDeletion.value.delete(blobSid);
  });
};

onMounted(() => {
  props.editor.on("update", handleEditorUpdate);
  props.editor.on("destroy", handleEditorDestroy);
});

onUnmounted(() => {
  props.editor.off("update", handleEditorUpdate);
  props.editor.off("destroy", handleEditorDestroy);
});

const showImageSearchEditor = ref(false);
const showAiImageSearch = ref(false);
const { imageAttachments: imageAttachtmentService } = useApiClient();

const handleImageUploadSuccess = async (blob: Partial<File> | FileInput) => {
  const response = await imageAttachtmentService.createImageAttachment({
    requestBody: {
      imageAttachment: {
        attachableType: props.parentType,
        attachableId: props.parentId,
        file: blob as FileInput,
      },
    },
  });

  addImageBlob(response.id);
};

const handleImageUploadError = (error: unknown) => {
  console.error(error);
};

const handleImageSearchSubmit = async (blob: Partial<File> | FileInput) => {
  showImageSearchEditor.value = false;
  showAiImageSearch.value = false;

  handleImageUploadSuccess(blob);
};

const addImageBlob = (signedId: string) => {
  const src = `/files/${signedId}`;
  props.editor.chain().focus().setImage({ src, blobSid: signedId }).run();
};

const BLOCK_TYPES = [
  {
    value: "p",
    label: "wysiwyg.blockTypes.paragraph",
  },
  {
    value: "h1",
    label: "wysiwyg.blockTypes.one",
  },
  {
    value: "h2",
    label: "wysiwyg.blockTypes.two",
  },
  {
    value: "h3",
    label: "wysiwyg.blockTypes.three",
  },
  {
    value: "h4",
    label: "wysiwyg.blockTypes.four",
  },
  {
    value: "h5",
    label: "wysiwyg.blockTypes.five",
  },
  {
    value: "h6",
    label: "wysiwyg.blockTypes.six",
  },
];

const currentBlockTypeLabel = computed(() => {
  if (props.editor.isActive("heading", { level: 1 })) {
    return "wysiwyg.blockTypes.one";
  } else if (props.editor.isActive("heading", { level: 2 })) {
    return "wysiwyg.blockTypes.two";
  } else if (props.editor.isActive("heading", { level: 3 })) {
    return "wysiwyg.blockTypes.three";
  } else if (props.editor.isActive("heading", { level: 4 })) {
    return "wysiwyg.blockTypes.four";
  } else if (props.editor.isActive("heading", { level: 5 })) {
    return "wysiwyg.blockTypes.five";
  } else if (props.editor.isActive("heading", { level: 6 })) {
    return "wysiwyg.blockTypes.six";
  } else if (props.editor.isActive("paragraph")) {
    return "wysiwyg.blockTypes.paragraph";
  } else {
    return "wysiwyg.blockTypes.paragraph";
  }
});
</script>
<template>
  <div class="toolbar-wrapper">
    <div
      v-if="editor"
      class="wysiwyg-toolbar-container"
      :class="{
        'wysiwyg-toolbar-container--navbar main-margin': navbar,
      }"
    >
      <TipTapEditorToolbar :naked="naked" :size="size" :container="navbar">
        <ToolbarButton
          v-for="button in undoRedoButtons"
          :key="button.icon"
          v-tooltip="t(button.label)"
          :active="false"
          fixed
          :aria-label="t(button.label)"
          @click.prevent="button.action"
        >
          <i :class="`bi bi-${button.icon}`"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarDropdown
          v-if="matchToolbarMode('full')"
          id="wysiwyg-block-type"
          v-tooltip="t('wysiwyg.actions.blockType')"
          class="d-none d-lg-flex"
          style="width: 120px"
          :button-aria-label="t('wysiwyg.actions.blockType')"
          border
        >
          <template #button>
            <span
              style="
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              "
              >{{ t(currentBlockTypeLabel) }}</span
            >
          </template>
          <BaseDropdownItem
            v-for="blockType in BLOCK_TYPES"
            :key="blockType.value"
            :value="blockType.value"
            @click="changeBlockType(blockType.value)"
          >
            {{ t(blockType.label) }}
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator size="sm" />

        <ToolbarDropdown
          v-if="matchToolbarMode('lg')"
          id="wysiwyg-font-family"
          v-tooltip="t('wysiwyg.actions.fontFamily')"
          class="d-none d-md-flex"
          style="width: 100px"
          :button-aria-label="t('wysiwyg.actions.fontFamily')"
          border
        >
          <template #button>
            <span
              style="
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              "
            >
              {{ currentFontFamilyLabel }}
            </span>
          </template>
          <BaseDropdownItem
            v-for="fontFamily in FONT_FAMILIES"
            :key="fontFamily.value"
            :value="fontFamily.value"
            @click="changeFontFamily(fontFamily.value)"
          >
            <span :style="{ fontFamily: fontFamily.value }">
              {{ fontFamily.label }}
            </span>
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator size="sm" />

        <ToolbarFontSizeDropdown
          v-if="matchToolbarMode('lg')"
          id="wysiwyg-font-size"
          v-tooltip="t('wysiwyg.actions.fontSize')"
          class="d-none d-sm-flex"
          border
          :button-aria-label="t('wysiwyg.actions.fontSize')"
          @increment="incrementFontSize"
          @decrement="decrementFontSize"
        >
          <template #button>
            <span v-if="currentFontSize" style="font-size: 0.9rem">
              {{ currentFontSize }}
            </span>
            <span v-else style="font-size: 0.9rem">16</span>
          </template>
          <BaseDropdownItem
            v-for="fontSize in FONT_SIZES"
            :key="fontSize.value"
            :value="fontSize.value"
            :aria-label="fontSize.label.toString()"
            @click="changeFontSize(fontSize.value)"
          >
            {{ fontSize.label }}
          </BaseDropdownItem>
        </ToolbarFontSizeDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in formatButtons"
          :key="button.icon"
          v-tooltip="t(button.label)"
          :active="button.isActive()"
          :aria-label="t(button.label)"
          fixed
          @click.prevent="button.action"
        >
          <i :class="`bi bi-${button.icon}`"></i>
        </ToolbarButton>

        <ToolbarDropdown
          id="wysiwyg-format-advanced"
          class="d-none d-md-block"
          :button-aria-label="currentFormatAdvanced?.label"
        >
          <template #button>
            <span v-if="currentFormatAdvanced">
              <i :class="`bi bi-${currentFormatAdvanced.icon}`"></i>
            </span>
            <span v-else></span>
          </template>
          <BaseDropdownItem
            v-for="formatAdvancedButton in formatAdvancedButtons"
            :key="formatAdvancedButton.value"
            :icon-name="formatAdvancedButton.icon"
            :label="t(formatAdvancedButton.label)"
            @click="formatAdvancedButton.action"
          >
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarDropdown
          id="wysiwyg-align"
          :button-aria-label="currentTextAlign?.label"
        >
          <template #button>
            <span v-if="currentTextAlign">
              <i :class="`bi bi-${currentTextAlign.icon}`"></i>
            </span>
          </template>
          <BaseDropdownItem
            v-for="alignButton in alignButtons"
            :key="alignButton.value"
            :icon-name="alignButton.icon"
            @click="changeTextAlign(alignButton.value || 'left')"
          >
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in listButtons"
          :key="button.icon"
          v-tooltip="t(button.label)"
          :active="button.isActive()"
          :aria-label="t(button.label)"
          fixed
          class="d-none d-md-flex"
          @click.prevent="button.action"
        >
          <i :class="`bi bi-${button.icon}`"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in linkButtons"
          :key="button.icon"
          v-tooltip="t(button.label)"
          :active="button.isActive()"
          :aria-label="t(button.label)"
          fixed
          class="d-none d-md-flex"
          @click.prevent="button.action"
        >
          <i :class="`bi bi-${button.icon}`"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarDropdown
          v-if="matchToolbarMode('md')"
          id="wysiwyg-more"
          v-tooltip="t('wysiwyg.actions.images')"
          fixed-min-width
          align="right"
          :button-attrs="{ 'data-testid': 'dropdown-images' }"
          :button-aria-label="t('wysiwyg.actions.images')"
        >
          <template #button>
            <span>
              <i class="bi bi-image"></i>
            </span>
          </template>

          <DirectUploadDropzone
            :allowed-types="['image/jpeg', 'image/png', 'image/gif']"
            :max-file-size="5"
            attachment-name="Block#images"
            shrine
            @upload:success="handleImageUploadSuccess"
            @upload:error="handleImageUploadError"
          >
            <template #default="{ openFileBrowser }">
              <BaseDropdownItem
                data-testid="link-open-file-browser"
                icon-name="image"
                @click.prevent="openFileBrowser"
              >
                {{ t("links.uploadImage") }}
              </BaseDropdownItem>
              <BaseDropdownItem
                icon-name="search"
                @click.prevent="showImageSearchEditor = true"
              >
                {{ t("links.imageSearch") }}
              </BaseDropdownItem>
              <BaseDropdownItem
                v-if="userStore.isFeatureEnabled('ai_image_search')"
                icon-name="robot"
                @click.prevent="showAiImageSearch = true"
              >
                {{ t("links.aiImageSearch") }}
              </BaseDropdownItem>
            </template>
          </DirectUploadDropzone>
        </ToolbarDropdown>

        <ToolbarDropdown
          v-if="matchToolbarMode('md')"
          id="wysiwyg-more"
          v-tooltip="t('wysiwyg.actions.more')"
          fixed-min-width
          align="right"
          class="d-none d-md-flex"
          :button-attrs="{ 'data-testid': 'dropdown-more' }"
          :button-aria-label="t('wysiwyg.actions.more')"
        >
          <template #button>
            <span>
              <i class="bi bi-plus-lg"></i>
            </span>
          </template>

          <template v-if="matchToolbarMode('md')">
            <BaseDropdownItem
              v-for="button in tableButtons"
              :key="button.icon"
              :icon-name="button.icon"
              @click.prevent="button.action"
            >
              {{ t(button.label) }}
            </BaseDropdownItem>
          </template>

          <template v-if="matchToolbarMode('lg')">
            <BaseDropdownItem
              v-for="button in advancedButtons"
              :key="button.icon"
              :icon-name="button.icon"
              @click.prevent="button.action"
            >
              {{ t(button.label) }}
            </BaseDropdownItem>
          </template>
          <template v-if="matchToolbarMode('full')">
            <BaseDropdownItem
              v-for="button in embedButtons"
              :key="button.icon"
              :icon-name="button.icon"
              @click.prevent="button.action"
            >
              {{ t(button.label) }}
            </BaseDropdownItem>
          </template>
        </ToolbarDropdown>

        <div class="d-flex align-items-center">
          <slot name="insert-actions"></slot>
        </div>

        <div class="d-flex align-items-center ms-auto">
          <slot name="hint"></slot>
        </div>
      </TipTapEditorToolbar>
    </div>
  </div>
  <StockImageSearchModal
    shrine
    :open="showImageSearchEditor"
    @close="showImageSearchEditor = false"
    @submit="handleImageSearchSubmit"
  />

  <AiImageSearchModal
    :open="showAiImageSearch"
    @close="showAiImageSearch = false"
    @submit="handleImageSearchSubmit"
  />

  <SpecialCharactersModal
    :open="isSpecialCharactersModalOpen"
    @hide="isSpecialCharactersModalOpen = false"
    @insert="insertCharacter"
  />
</template>

<style lang="scss" scoped>
.toolbar-wrapper {
  display: flex;
  visibility: visible;
}

.wysiwyg-toolbar {
  flex-wrap: nowrap;
  justify-content: space-between;
}

.wysiwyg-toolbar-container {
  position: relative;
  width: 100%;

  &--navbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    width: 100%;
    padding: 0 $spacer-3;
  }

  &--fixed {
    position: fixed;
    top: $header-height-mobile;
    left: 0;
    right: 0;
    z-index: 1;
    max-width: 1024px;

    // space filled between fixed header and wysiwyg-toolbar
    padding-top: 2rem;
    background-color: $body-bg;
    background-image: radial-gradient(#eee 0.5px, transparent 0.5px);
    background-size: 5px 5px;
  }
}
</style>
